import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronRight, FaCheck, FaPhone, FaEnvelope, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';

const GetStartedPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    "Digital Advertising",
    "Content Marketing",
    "Social Media Management",
    "Influencer Marketing",
    "Brand Strategy",
    "Press Release Distribution"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    }
    if (step === 2 && !formData.service) {
      newErrors.service = 'Please select a service';
    }
    return newErrors;
  };



  const nextStep = () => {
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const stepErrors = validateStep(3);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    setIsSubmitting(true); // Start loader

    try {
      const response = await fetch('https://vibex.media/api/send_getstarted_email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
      } else {
        setErrors({ message: result.message || "Something went wrong!" });
      }
    } catch (err) {
      setErrors({ message: "Server error. Please try again later." });
      console.error("Error sending form:", err);
    } finally {
      setIsSubmitting(false); // Stop loader
    }
  };

  return (
    <section className="get-started-page py-5">
      <div className="container">
        <div className="row mb-5">
          <div className="col-lg-8 mx-auto text-center">
            <h1 className="display-4 fw-bold mb-3">Ready to Grow Your Business?</h1>
            <p className="lead text-muted">Let's start your journey with us. Complete this quick form and we'll get back to you within 24 hours.</p>
          </div>
        </div>

        {/* Progress Tracker */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="progress-steps">
              <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
                <div className="step-number">{currentStep > 1 ? <FaCheck /> : '1'}</div>
                <div className="step-label">Your Information</div>
              </div>
              <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
                <div className="step-number">{currentStep > 2 ? <FaCheck /> : '2'}</div>
                <div className="step-label">Service Selection</div>
              </div>
              <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
                <div className="step-number">3</div>
                <div className="step-label">Project Details</div>
              </div>
            </div>
          </div>
        </div>

        {isSubmitted ? (
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="card border-0 shadow-sm p-5 text-center">
                <div className="success-icon bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-4">
                  <FaCheck size={24} />
                </div>
                <h2 className="mb-3">Thank You for Reaching Out!</h2>
                <p className="text-muted mb-4">
                  We've received your information and our team will contact you within 24 hours to discuss your project.
                </p>
                <div className="d-flex justify-content-center gap-3">
                  <a href="/" className="btn btn-outline-primary">
                    Back to Home
                  </a>
                  <Link to="/contact" className="btn btn-primary">
                    Contact Page
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="card border-0 shadow-sm p-4 p-lg-5">
                <form onSubmit={handleSubmit}>
                  {/* Step 1: Contact Information */}
                  {currentStep === 1 && (
                    <div className="step-content">
                      <h3 className="h4 mb-4">Tell Us About Yourself</h3>
                      <div className="mb-4">
                        <label htmlFor="name" className="form-label">Full Name *</label>
                        <input
                          type="text"
                          className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-4">
                          <label htmlFor="email" className="form-label">Email Address *</label>
                          <input
                            type="email"
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                          />
                          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>
                        <div className="col-md-6 mb-4">
                          <label htmlFor="phone" className="form-label">Phone Number *</label>
                          <input
                            type="tel"
                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                          />
                          {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                        </div>
                      </div>
                      <div className="mb-4">
                        <label htmlFor="company" className="form-label">Company Name</label>
                        <input
                          type="text"
                          className="form-control"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={nextStep}
                        >
                          Next <FaChevronRight className="ms-2" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Service Selection */}
                  {currentStep === 2 && (
                    <div className="step-content">
                      <h3 className="h4 mb-4">Select Your Service</h3>
                      {errors.service && <div className="alert alert-danger">{errors.service}</div>}
                      <div className="row g-3 mb-4">
                        {services.map((service, index) => (
                          <div key={index} className="col-md-6">
                            <div
                              className={`service-option card p-3 ${formData.service === service ? 'selected' : ''}`}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, service }));
                                if (errors.service) setErrors({ ...errors, service: '' });
                              }}
                            >
                              <div className="form-check">
                                <input
                                  type="radio"
                                  className="form-check-input"
                                  name="service"
                                  id={`service-${index}`}
                                  checked={formData.service === service}
                                  onChange={() => { }}
                                />
                                <label className="form-check-label" htmlFor={`service-${index}`}>
                                  {service}
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="d-flex justify-content-between">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={prevStep}
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={nextStep}
                        >
                          Next <FaChevronRight className="ms-2" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Project Details */}
                  {currentStep === 3 && (
                    <div className="step-content">
                      <h3 className="h4 mb-4">Tell Us About Your Project</h3>
                      <div className="mb-4">
                        <label htmlFor="message" className="form-label">Project Details *</label>
                        <textarea
                          className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                          id="message"
                          name="message"
                          rows="5"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your goals, timeline, budget, and any other relevant details..."
                        ></textarea>
                        {errors.message && <div className="invalid-feedback">{errors.message}</div>}
                      </div>
                      <div className="d-flex justify-content-between">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={prevStep}
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Sending...
                            </>
                          ) : (
                            <>
                              <span className="ms-2">Submit Request</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Alternative Contact Options */}
        {!isSubmitted && (
          <div className="row mt-5">
            <div className="col-12">
              <div className="card border-0 shadow-sm p-4 p-lg-5">
                <h2 className="h3 mb-4 text-center">Prefer Other Contact Methods?</h2>
                <div className="row g-4">
                  <div className="col-md-4">
                    <div className="contact-option text-center p-4">
                      <div className="contact-icon bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3">
                        <FaPhone size={20} />
                      </div>
                      <h3 className="h5 mb-2">Call Us</h3>
                      <p className="text-muted mb-3">Speak directly with our team</p>
                      <a href="tel:+1 (613) 915-9806" className="btn btn-outline-primary">
                        +1 (613) 915-9806
                      </a>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="contact-option text-center p-4">
                      <div className="contact-icon bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3">
                        <FaEnvelope size={20} />
                      </div>
                      <h3 className="h5 mb-2">Email Us</h3>
                      <p className="text-muted mb-3">Send us your inquiry</p>
                      <a href="mailto:contact@vibex.media" className="btn btn-outline-primary">
                        contact@vibex.media
                      </a>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="contact-option text-center p-4">
                      <div className="contact-icon bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3">
                        <FaCalendarAlt size={20} />
                      </div>
                      <h3 className="h5 mb-2">Book a Meeting</h3>
                      <p className="text-muted mb-3">Schedule a consultation</p>
                      <a href="/schedule" className="btn btn-outline-primary">
                        Schedule Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default GetStartedPage;